import React, { useRef, useState, useEffect, ReactElement } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../contexts/auth.context";
import { jwtDecode } from "jwt-decode";
import {
  Button,
  TextField,
  Box,
  Typography,
  Paper,
  Alert,
  Container,
} from "@mui/material";
import { API_ENDPOINTS } from "../config";

const Login = (): ReactElement => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/";

  const userRef = useRef<HTMLInputElement>(null);
  const errRef = useRef<HTMLDivElement>(null);

  const [user, setUser] = useState<string>("");
  const [pwd, setPwd] = useState<string>("");
  const [errMsg, setErrMsg] = useState<string>("");

  useEffect(() => {
    userRef.current?.focus();
  }, []);

  useEffect(() => {
    setErrMsg("");
  }, [user, pwd]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      const response = await fetch(API_ENDPOINTS.LOGIN_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        credentials: "include",
        body: new URLSearchParams({ username: user, password: pwd }),
      });

      if (!response.ok) {
        throw new Error(
          response.status === 400
            ? "Please enter both username and password."
            : response.status === 401
              ? "Incorrect username or password."
              : response.status === 429
                ? "Too many login attempts. Please wait a moment and try again."
                : response.status >= 500
                  ? "The login service is temporarily unavailable. Please try again shortly."
                  : "Login failed. Please try again.",
        );
      }

      const data = await response.json();
      const decodedToken = jwtDecode(data.access) as { roles: string[] };
      const authData = {
        user,
        roles: decodedToken.roles,
        accessToken: data.access,
      };

      // console.log("🔹 Login Successful:", authData);
      login(authData);

      setUser("");
      setPwd("");
      navigate(from, { replace: true });
    } catch (err) {
      setErrMsg(err instanceof Error ? err.message : "No Server Response");
      errRef.current?.focus();
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
            borderRadius: 2,
          }}
        >
          <Typography
            component="h1"
            variant="h4"
            sx={{ mb: 3, fontWeight: "bold" }}
          >
            Sign In
          </Typography>

          {errMsg && (
            <Alert ref={errRef} severity="error" sx={{ width: "100%", mb: 2 }}>
              {errMsg}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%" }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="username"
              label="Username"
              name="username"
              autoComplete="username"
              autoFocus
              value={user}
              onChange={(e) => setUser(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#f5f5f5",
                  "&:hover": {
                    backgroundColor: "#eeeeee",
                  },
                  "&.Mui-focused": {
                    backgroundColor: "#ffffff",
                  },
                },
              }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#f5f5f5",
                  "&:hover": {
                    backgroundColor: "#eeeeee",
                  },
                  "&.Mui-focused": {
                    backgroundColor: "#ffffff",
                  },
                },
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{
                mt: 3,
                mb: 2,
                py: 1.5,
                fontSize: "1.1rem",
                textTransform: "none",
                fontWeight: "bold",
              }}
            >
              Sign In
            </Button>

            <Box sx={{ textAlign: "center", mt: 2 }}>
              <Typography variant="body2">
                Need an Account?{" "}
                <Link
                  to="/register"
                  style={{
                    color: "#1976d2",
                    textDecoration: "none",
                    fontWeight: "bold",
                  }}
                >
                  Sign Up
                </Link>
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login;
